import React, { useState, useRef } from "react";
import { string, bool, number, arrayOf, shape } from "prop-types";
import { useCombobox } from "downshift";
import Icon from "../Icon/Icon.jsx";
import ArrowLink from "../ArrowLink/ArrowLink.jsx";
import cx from "classnames";

const propTypes = {
  showSearchIcon: bool,
  placeholder: string,
  className: string,
  minCharsForFilter: number,
  noResultsString: string,
  items: arrayOf(
    shape({
      label: string,
    }),
  ),
};

const TypeaheadSearch = (props) => {
  const {
    showSearchIcon,
    placeholder,
    className,
    items,
    noResultsString,
    minCharsForFilter = 3,
  } = props;

  const [inputItems, setInputItems] = useState(items);
  const [inputValue, setInputValue] = useState("");
  const [selectedItem, setSelectedItem] = useState({});
  const [inputVisible, setInputVisible] = useState(false);

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: inputItems.map((input) => input.label),
    onSelectedItemChange: (changes) => {
      const { selectedItem } = changes;

      const selected = inputItems.find((input) => input.label === selectedItem);

      if (selected.href) {
        window.location = selected.href;
      } else {
        setInputValue(changes.selectedItem.label);
      }
    },
    onInputValueChange: ({ inputValue }) => {
      setInputValue(inputValue);
      setInputItems(
        items.filter(
          (item) =>
            inputValue.length >= minCharsForFilter &&
            item.label.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1,
        ),
      );
    },
  });

  const selfRef = useRef();

  const handleToggleClick = () => {
    let parent = selfRef.current.closest(".js-typeahead-parent");
    if (inputVisible) {
      if (parent) {
        parent.classList.remove("is-open");
      }
      setInputVisible(false);
    } else {
      if (parent) {
        parent.classList.add("is-open");
      }
      setInputVisible(true);
      // delaying focus on typeahead field to allow animation to complete for better UX
      setTimeout(() => {
        selfRef.current.querySelector("input").focus();
      }, 400);
    }
  };

  return (
    <div
      ref={selfRef}
      className={cx("typeahead-search", className, {
        "typeahead-search--visible": inputVisible,
      })}
    >
      <div className="typeahead-search__combo" {...getComboboxProps()}>
        <input
          aria-label={placeholder}
          placeholder={placeholder}
          className="typeahead-search__input"
          {...getInputProps()}
        />
        {showSearchIcon && (
          <div className="typeahead-search__search-icon">
            <Icon iconName="search" />
          </div>
        )}
      </div>
      {showSearchIcon && (
        <button
          type="button"
          onClick={handleToggleClick}
          aria-expanded={inputVisible}
          className="btn typeahead-search__toggle-button"
          aria-controls={getInputProps().id}
        >
          <Icon iconName="search" />
          <Icon iconName="close" />
          <span className="u-visuallyhidden">toggle search input</span>
        </button>
      )}
      <ul
        className={cx("typeahead-search__results", {
          "typeahead-search__results--open": isOpen,
        })}
        style={{
          display: isOpen ? "block" : "none",
        }}
        {...getMenuProps()}
      >
        {inputItems.length > 0 &&
          inputItems.map((item, index) => (
            <li
              style={
                highlightedIndex === index ? { backgroundColor: "#bde4ff" } : {}
              }
              key={`${item.label}${index}`}
              {...getItemProps({ item, index })}
            >
              <ArrowLink
                href={item.href}
                text={item.label}
                type={item.externalLink ? "external" : ""}
              />
            </li>
          ))}

        {inputItems.length === 0 && inputValue.length < 3 && (
          <li className="typeahead-search__pending-results-row">
            Please type at least 3 letters
          </li>
        )}

        {inputItems.length === 0 && inputValue.length >= 3 && (
          <li className="typeahead-search__pending-results-row">
            {noResultsString || "No results found"}
          </li>
        )}
      </ul>
    </div>
  );
};

TypeaheadSearch.propTypes = propTypes;
export default TypeaheadSearch;
